import type { ReactNode } from "react";
import {
  ArrowUpRight,
  ArrowDownRight,
  CalendarIcon,
  ChevronDown,
  Compass,
  ExternalLink,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

// Non-technical walkthrough for field leaders / hospital managers. Deliberately
// this page teaches the workflow visually — each section leads with
// a static mockup of the real UI (bottom half of this file) and a one-line
// takeaway.
//
// Ordered around the app's repeating component vocabulary rather than by topic:
// the controls, then the four building blocks that behave identically on every
// tab, then only what's unique per tab. Scoped to Capacity & Utilization and
// Revenue for now.

const TEAL = "var(--brand-dark-teal)";
const CREAM_TINT = "color-mix(in srgb, var(--brand-cream) 55%, white)";

const PURVIEW_URL =
  "https://purview.microsoft.com/datacatalog/governance/main/catalog/searchData/result/dataProduct?tid=29ecdc93-e9fc-4cf4-9d3f-b4af8d2e140c&dataProductId=a73efef4-5dd8-461d-a809-fd967a6bc7e6&sourceDomainId=52b3554f-2db0-4016-bfb9-aad7af5d56bc";

// Same figures as section 03's detailed card, so the compact teaser in Quick
// start and the anatomy reference read as the same card.
const PETS_SEEN_ROWS = [
  { label: "vs. PY", baseline: "3.9 h", change: "+0.3 h", delta: "+7.7%", up: true },
  { label: "vs. NVA avg", baseline: "4.4 h", change: "-0.2 h", delta: "-4.5%", up: false },
];

function Masthead() {
  return (
    <header
      className="relative overflow-hidden rounded-2xl px-8 py-8"
      style={{ background: "linear-gradient(135deg, #044f59 0%, #06626e 55%, #0a7c8a 100%)" }}
    >
      <Compass
        className="absolute -right-10 -top-10 h-52 w-52 text-white opacity-[0.08]"
        strokeWidth={1}
      />
      <div className="relative">
        <span
          className="text-[10px] font-semibold uppercase tracking-[1.4px]"
          style={{ color: "color-mix(in srgb, var(--brand-light-teal) 85%, transparent)" }}
        >
          Field guide
        </span>
        <h1 className="mt-2 text-[30px] font-semibold tracking-tight text-white leading-[1.15]">
          Making sense of Fetch Time 2.0
        </h1>
        <div
          className="h-px w-16 my-4"
          style={{ background: "color-mix(in srgb, var(--brand-light-teal) 55%, transparent)" }}
        />
        <p
          className="max-w-[560px] text-[14px] leading-relaxed"
          style={{ color: "color-mix(in srgb, var(--brand-cream) 82%, transparent)" }}
        >
          Fetch Time 2.0 turns your schedule into a picture of where doctor time goes — and whether
          that's normal. Capacity &amp; Utilization and Revenue answer that from different angles,
          and both are built from the same few pieces.
        </p>
      </div>
    </header>
  );
}

function Section({
  n,
  title,
  lede,
  children,
}: {
  n: string;
  title: string;
  lede: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="grid grid-cols-[46px_1fr] gap-x-5 border-t border-black/[0.07] pt-7">
      <div
        className="text-[28px] font-bold leading-none tabular-nums select-none"
        style={{ color: "color-mix(in srgb, var(--brand-mid-teal) 38%, transparent)" }}
      >
        {n}
      </div>
      <div className="min-w-0">
        <h2 className="text-[17px] font-semibold tracking-tight" style={{ color: TEAL }}>
          {title}
        </h2>
        <p className="mt-1 text-[13.5px] text-gray-500 leading-relaxed max-w-[620px]">{lede}</p>
        <div className="mt-5">{children}</div>
      </div>
    </section>
  );
}

function Plinth({ children, caption }: { children: ReactNode; caption?: ReactNode }) {
  return (
    <figure>
      <div className="rounded-xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        {children}
      </div>
      {caption && <figcaption className="mt-2 text-[11.5px] text-gray-400">{caption}</figcaption>}
    </figure>
  );
}

// First external link in the app — opens in a new tab and says so, since it
// leaves FetchTime for the Purview catalog.
function DocLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 font-medium underline decoration-from-font underline-offset-2 hover:opacity-75 transition-opacity"
      style={{ color: TEAL }}
    >
      {children}
      <ExternalLink className="h-3 w-3 shrink-0" />
    </a>
  );
}

function Callout({ children }: { children: ReactNode }) {
  return (
    <div
      className="rounded-r-md px-3.5 py-2.5 text-[12.5px] text-gray-600"
      style={{ background: CREAM_TINT, borderLeft: `3px solid ${TEAL}` }}
    >
      {children}
    </div>
  );
}

function StepCircle({ n }: { n: number }) {
  return (
    <div
      className="grid place-items-center rounded-full text-white text-[12px] font-semibold shrink-0"
      style={{ width: 22, height: 22, background: TEAL }}
    >
      {n}
    </div>
  );
}

function Step({
  n,
  title,
  hint,
  children,
}: {
  n: number;
  title: string;
  hint: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 mb-1">
        <StepCircle n={n} />
        <span className="text-[13.5px] font-semibold text-gray-800">{title}</span>
      </div>
      <p className="text-[12px] text-gray-400 mb-2.5 min-h-[46px] leading-snug">{hint}</p>
      <div className="flex-1 rounded-xl border border-black/[0.08] bg-white p-3.5 flex items-center justify-center shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        {children}
      </div>
    </div>
  );
}

const TRAILING_DEFS: [string, string][] = [
  ["T1W", "the last full week"],
  ["T4W", "the last 4 full weeks (about a month)"],
  ["T13W", "the last 13 full weeks (a quarter)"],
  ["T52W", "the last 52 full weeks (a year)"],
];

const CALENDAR_DEFS: [string, string][] = [
  ["PM", "the previous calendar month"],
  ["MTD", "this month so far"],
  ["YTD", "this year so far"],
];

const GROUP_BY_DEFS: [string, string][] = [
  ["Day", "a single day"],
  ["Week", "one Monday–Sunday week"],
  ["Month", "the whole weeks that belong to that month"],
];

// Numbers match the Marker badges rendered inside MiniDrillTable.
const DRILL_CALLOUTS: [string, string][] = [
  [
    "Export CSV",
    "downloads every row in both units at once — % and hours — not just the one on screen",
  ],
  ["Unit toggle", "% · Hours"],
  ["Grain", "Country · Group · Division · Site · DVM"],
  [
    "Column headers",
    "click any one to sort — the fastest way to surface the site or doctor worth a conversation",
  ],
  [
    "Pinned averages",
    "one row per level you're inside — pick a group and a division and their averages pin here too",
  ],
  [
    "Green and red",
    "the top and bottom 20% at the grain you are on — of every site at Site level, of every " +
      "doctor at DVM level — not just the rows on screen, and narrowed to the kept sites " +
      "when PIMS Launched Before is set",
  ],
  [
    "Click a row",
    "zooms in: it filters the whole dashboard to that row, fills in the levels above it, and " +
      "opens the next grain down so you see what is inside what you clicked. Clear it from the " +
      "chip up top",
  ],
];

export function GuideTab() {
  return (
    <div className="max-w-[1000px] pb-12">
      <Masthead />

      <div className="mt-8 flex flex-col gap-7">
        <Section n="01" title="Quick start" lede="Three moves, every time you open the dashboard.">
          <div
            className="flex items-center gap-3 flex-wrap rounded-lg px-3.5 py-3 mb-5"
            style={{ background: CREAM_TINT }}
          >
            <MiniFiltersButton />
            <span className="text-[12.5px] text-gray-600">
              Scope and period both live behind the{" "}
              <strong className="font-semibold">Filters</strong> button, top right — click it to
              show or hide them.
            </span>
          </div>
          <div className="grid grid-cols-3 gap-4 items-stretch">
            <Step
              n={1}
              title="Pick scope"
              hint="Country, Group, Division, Site, DVM — each one narrows the whole page."
            >
              <div className="flex flex-col items-center gap-2.5 w-full">
                <MiniScopeDropdowns />
                <div className="h-px w-full bg-black/[0.06]" />
                <MiniFilterChip label="Site: 1234 - Main St" />
                <span className="text-[10px] text-gray-400 text-center leading-snug">
                  Filters show as chips up top — click the{" "}
                  <strong className="font-semibold">×</strong> to remove one
                </span>
              </div>
            </Step>
            <Step
              n={2}
              title="Pick period"
              hint={
                <>
                  Trailing windows use whole{" "}
                  <strong className="font-semibold" style={{ color: TEAL }}>
                    Monday–Sunday
                  </strong>{" "}
                  weeks; calendar periods follow the month.
                </>
              }
            >
              <div className="flex flex-col items-center gap-2.5 w-full">
                <MiniPeriodBar compact />
                <div className="h-px w-full bg-black/[0.06]" />
                <MiniSelectedRangeCard />
                <span className="text-[10px] text-gray-400 text-center leading-snug">
                  Check exact dates here
                </span>
              </div>
            </Step>
            <Step
              n={3}
              title="Read the cards"
              hint="Every number carries two comparisons — your own history, and the company."
            >
              <MiniKpiCard
                label="Pets seen/DVM/day"
                value="4.2"
                unit="hrs"
                labelColor="var(--perf-green-dark)"
                rows={PETS_SEEN_ROWS}
              />
            </Step>
          </div>
        </Section>

        <Section
          n="02"
          title="Understanding the schedule periods"
          lede={
            <>
              Clinics and doctors work to a weekly rotation — the same shifts repeat each week. So
              the periods come in two families:{" "}
              <strong className="font-semibold" style={{ color: TEAL }}>
                trailing weeks
              </strong>{" "}
              for fair comparisons, and{" "}
              <strong className="font-semibold" style={{ color: TEAL }}>
                calendar
              </strong>{" "}
              periods for reporting.
            </>
          }
        >
          <Plinth caption="Every period counts back from the As of date, not from today.">
            <MiniPeriodBar />
            <div className="grid grid-cols-2 gap-x-8 mt-4">
              <div>
                <div
                  className="text-[10px] font-semibold uppercase tracking-[0.7px] mb-2"
                  style={{ color: TEAL }}
                >
                  Trailing full weeks
                </div>
                <div className="flex flex-col gap-1.5 text-[12.5px] text-gray-500">
                  {TRAILING_DEFS.map(([k, v]) => (
                    <div key={k}>
                      <strong className="text-gray-700">{k}</strong> — {v}
                    </div>
                  ))}
                </div>
                <p className="mt-2.5 text-[11.5px] text-gray-400 leading-relaxed">
                  Whole Monday–Sunday weeks, every time — never a partial week on either side.
                </p>
              </div>
              <div>
                <div
                  className="text-[10px] font-semibold uppercase tracking-[0.7px] mb-2"
                  style={{ color: TEAL }}
                >
                  Calendar to-date
                </div>
                <div className="flex flex-col gap-1.5 text-[12.5px] text-gray-500">
                  {CALENDAR_DEFS.map(([k, v]) => (
                    <div key={k}>
                      <strong className="text-gray-700">{k}</strong> — {v}
                    </div>
                  ))}
                </div>
                <p className="mt-2.5 text-[11.5px] text-gray-400 leading-relaxed">
                  Follows the calendar, so the first and last weeks may be partial.
                </p>
              </div>
            </div>
          </Plinth>
          <div className="mt-3">
            <Callout>
              <strong className="font-semibold" style={{ color: TEAL }}>
                Comparing?
              </strong>{" "}
              Use a trailing window — equal weeks on both sides, so year-over-year lines up rotation
              against rotation.{" "}
              <strong className="font-semibold" style={{ color: TEAL }}>
                Reporting to a monthly or annual number?
              </strong>{" "}
              Use the calendar periods.
            </Callout>
          </div>
          <div className="mt-5">
            <Plinth caption="Sits under the period buttons.">
              <div className="text-[12.5px] font-semibold mb-2" style={{ color: TEAL }}>
                PIMS Launched Before
              </div>
              <p className="text-[12.5px] leading-relaxed text-gray-500">
                Keeps only sites whose PIMS go-live was on or before the date you pick. Sites that
                launched after it are left out of the whole dashboard.
              </p>
              <p className="mt-2.5 text-[12.5px] leading-relaxed text-gray-500">
                Once you pick a date, the note below the calendar is how many sites the cutoff keeps
                in total.
              </p>
            </Plinth>
          </div>
        </Section>

        <Section
          n="03"
          title="Capacity & Utilization"
          lede="Where the worked day actually goes — every minute of it lands in exactly one of three buckets."
        >
          <Plinth>
            <MiniBreakdownBar />
          </Plinth>
          <div className="mt-5">
            <Plinth>
              <div className="text-[12.5px] font-semibold mb-3" style={{ color: TEAL }}>
                How the worked days break down
              </div>
              <MiniEquation which="usage" />
              <p className="mt-3 text-[12px] text-gray-500 leading-relaxed">
                Worked hours are the hours actually worked, which is not the same as the hours on
                the roster — doctors start early and run over. Utilization divides by worked hours,
                so the three buckets above sum to exactly 100%. Capacity divides by SCHEDULED hours
                instead, because the only thing knowable about future capacity is what has been put
                on the schedule. That is why the two tabs report slightly different hours per DVM
                per day, and neither is wrong.
              </p>
              <div className="h-px bg-black/[0.06] my-4" />
              <div className="text-[12.5px] font-semibold" style={{ color: TEAL }}>
                How the scheduled weeks add up
              </div>
              <p className="mt-1 mb-3 text-[12px] text-gray-500 leading-relaxed">
                A change here means either longer days or more days on the schedule.
              </p>
              <MiniEquation which="hours" />
            </Plinth>
          </div>
        </Section>

        <Section
          n="04"
          title="Revenue"
          lede="What that time produced. The same revenue divided four ways, plus the visit count it is divided by — and the gaps between them are usually where the answer is."
        >
          <Plinth
            caption={
              <>
                Every figure here is{" "}
                <strong className="font-semibold" style={{ color: TEAL }}>
                  In-Clinic Medical Net Revenue
                </strong>
                , which excludes wellness services.
              </>
            }
          >
            <MiniRevenueMetrics />
          </Plinth>
        </Section>

        <Section
          n="05"
          title="KPI cards"
          lede="A headline number, then the same two yardsticks on every card — your own history, and the company."
        >
          <div className="grid grid-cols-[1fr_1fr] gap-6 items-start">
            <Plinth
              caption={
                <>
                  Hover any card for a short definition of its metric. Fuller definitions live in{" "}
                  <DocLink href={PURVIEW_URL}>Purview</DocLink>.
                </>
              }
            >
              <div className="flex justify-center">
                <MiniKpiCard
                  detailed
                  label="Pets seen/DVM/day"
                  value="4.2"
                  unit="hrs"
                  labelColor="var(--perf-green-dark)"
                  tooltip="Time in which a completed pet visit occurred."
                  rows={PETS_SEEN_ROWS}
                />
              </div>
            </Plinth>
            <div>
              <div className="flex flex-col gap-1.5 text-[12.5px] text-gray-500">
                <div>
                  <strong className="text-gray-700">vs. PY</strong> — your own history, back to your
                  Vetcove PIMS go-live
                </div>
                <div>
                  <strong className="text-gray-700">vs. NVA avg</strong> — all Vetcove sites, or
                  just the ones kept by <strong>PIMS Launched Before</strong> when that is set
                </div>
              </div>
              <p className="mt-2 text-[11.5px] text-gray-400 leading-relaxed">
                Sites that migrated recently may have a partial prior year, or none — which is what{" "}
                <strong className="font-semibold text-gray-500">PIMS Launched Before</strong> is
                for. See §02.
              </p>
              <div className="mt-4 rounded-lg border border-black/[0.08] bg-white px-3.5 py-3">
                <div className="text-[10px] font-semibold uppercase tracking-[0.7px] text-gray-400 mb-2.5">
                  What the pill color means
                </div>
                <div className="flex flex-col gap-2 text-[12px] text-gray-600">
                  <span className="flex items-center gap-2">
                    <MiniDeltaPill up value="+5.2%" />
                    better than the comparison
                  </span>
                  <span className="flex items-center gap-2">
                    <MiniDeltaPill up={false} value="-5.2%" />
                    worse than the comparison
                  </span>
                </div>
                <p className="mt-2.5 text-[11.5px] text-gray-500 leading-relaxed">
                  For metrics where less is better — Block-offs, Available time — a drop shows
                  green:{" "}
                  <span className="inline-flex align-middle">
                    <MiniDeltaPill up={false} value="-8.0%" better />
                  </span>
                </p>
              </div>
            </div>
          </div>
        </Section>

        <Section
          n="06"
          title="Trend charts"
          lede="Every trend chart gives you the same two controls: what's measured, and how it's grouped over time."
        >
          <Plinth>
            <MiniChartControls metrics={["%", "Hours"]} activeMetric="%" />
          </Plinth>
          <div className="mt-4 flex flex-col gap-1.5 text-[12.5px] text-gray-500">
            {GROUP_BY_DEFS.map(([k, v]) => (
              <div key={k}>
                <strong className="text-gray-700">{k}</strong> — one point covers {v}
              </div>
            ))}
          </div>
          <p className="mt-3 text-[12.5px] text-gray-500 leading-relaxed">
            Weeks are never split. A week belongs to the month where{" "}
            <strong className="font-semibold" style={{ color: TEAL }}>
              most of its days
            </strong>{" "}
            land — not where it ends.
          </p>
          <div className="mt-3">
            <Plinth caption="Which is why a month holds four or five weeks rather than a fixed number.">
              <MiniWeekSplit />
            </Plinth>
          </div>
          <p className="mt-3 text-[12.5px] text-gray-500 leading-relaxed">
            Quickest way to check any week yourself:{" "}
            <strong className="font-semibold" style={{ color: TEAL }}>
              look at its Thursday
            </strong>
            . Whichever month the Thursday lands in is the month the week counts as — it gives the
            same answer as counting days, every time, because a Monday–Sunday week can only have
            most of its days in the month that holds its Thursday.
          </p>
        </Section>

        <Section
          n="07"
          title="Drill-down table"
          lede="One table, five levels of detail — from the whole company down to a single doctor."
        >
          <Plinth>
            <MiniDrillTable annotated />
          </Plinth>
          <div className="mt-4 flex flex-col gap-2">
            {DRILL_CALLOUTS.map(([term, detail], i) => (
              <div
                key={term}
                className="flex items-start gap-2 text-[12.5px] leading-5 text-gray-500"
              >
                {/* Fixed-height wrapper centres the badge on the first line, so it
                    stays aligned when the text wraps. */}
                <span className="flex h-5 items-center shrink-0">
                  <Marker n={i + 1} />
                </span>
                <span>
                  <strong className="text-gray-700">{term}</strong> — {detail}
                </span>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Illustrations
//
// The static figures the sections above point at. Hardcoded display values
// only — no hooks, no props from live state, no fetching: this page has to
// keep working when the data layer is down.
// ---------------------------------------------------------------------------

// Static, illustrative mockups of real dashboard pieces — hardcoded display
// values only, never live data or hooks. They let the Guide tab show the real
// visual language instead of describing it. Colors and shapes are copied from
// the components they stand in for (KpiCard, UtilizationKPICards,
// UtilizationBreakdown, ScopeBar, PeriodBar, FilterBar, DrillDownTable,
// UtilizationChart, RevenueTab); keep them in sync if those change materially.

const TOGGLE_PILL =
  "text-[11px] px-2.5 py-1 rounded-md border font-medium transition-colors whitespace-nowrap";

function togglePill(active: boolean) {
  return active
    ? `${TOGGLE_PILL} bg-[var(--brand-dark-teal)] text-white border-[var(--brand-dark-teal)]`
    : `${TOGGLE_PILL} bg-white text-gray-600 border-black/15`;
}

// `up` drives the arrow (which way the number moved); `better` drives the color
// (whether that's good news). They differ for metrics where less is better, so
// a green down-arrow is valid. Defaults to higher-is-better.
function MiniDeltaPill({
  up,
  value,
  better = up,
}: {
  up: boolean;
  value: string;
  better?: boolean;
}) {
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-[1px] text-[11px] font-bold tabular-nums"
      style={{
        background: better
          ? "color-mix(in srgb, var(--perf-green) 18%, white)"
          : "color-mix(in srgb, var(--perf-red) 18%, white)",
        color: better ? "var(--perf-green-dark)" : "var(--perf-red-dark)",
        border: "1px solid rgba(0, 0, 0, 0.12)",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.06)",
      }}
    >
      <Icon className="h-3 w-3" />
      {value}
    </span>
  );
}

const BUCKETS = [
  {
    label: "Pets seen",
    hint: "time in completed appointments",
    pct: 61,
    color: "var(--perf-green)",
    text: "var(--perf-green-dark)",
  },
  {
    label: "Block-offs",
    hint: "intentionally closed — lunch, training",
    pct: 17,
    color: "var(--perf-red)",
    text: "var(--perf-red-dark)",
  },
  {
    label: "Available",
    hint: "never booked, or booked then cancelled",
    pct: 22,
    color: "var(--perf-gray)",
    text: "#555",
  },
];

function MiniBreakdownBar() {
  return (
    <div>
      <div className="flex rounded-md overflow-hidden h-9">
        {BUCKETS.map((b) => (
          <div
            key={b.label}
            className="flex items-center justify-center text-[12px] font-semibold text-white tabular-nums"
            style={{ width: `${b.pct}%`, background: b.color }}
          >
            {b.pct}%
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-col gap-2">
        {BUCKETS.map((b) => (
          <div key={b.label} className="flex items-baseline gap-2">
            <span
              className="w-2.5 h-2.5 rounded-[3px] shrink-0 translate-y-[1px]"
              style={{ background: b.color }}
            />
            <span className="text-[12.5px] font-semibold" style={{ color: b.text }}>
              {b.label}
            </span>
            <span className="text-[12px] text-gray-400">{b.hint}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 pt-2.5 border-t border-black/[0.06] text-[11.5px] text-gray-500">
        <strong className="font-semibold text-gray-600">Block-offs and Available</strong> are where
        unused capacity sits.
      </div>
    </div>
  );
}

// Mirrors the `= + +` operator layout the real UtilizationKPICards renders
// between its cards, which is what makes the tab's arithmetic legible.
// `hours` is an exact identity, not an approximation:
//   (dvmHours/dvmDays) × (dvmDays/(weeks×dvms)) = dvmHours/(weeks×dvms)
const EQUATIONS = {
  usage: [
    { label: "Worked Hours", color: "#333" },
    { op: "=" },
    { label: "Pets seen", color: "var(--perf-green-dark)" },
    { op: "+" },
    { label: "Block-offs", color: "var(--perf-red-dark)" },
    { op: "+" },
    { label: "Available", color: "#555" },
  ],
  hours: [
    { label: "Hrs/DVM/wk", color: "#333" },
    { op: "=" },
    { label: "Hrs/DVM/day", color: "#555" },
    { op: "×" },
    { label: "Days/wk", color: "#555" },
  ],
};

function MiniEquation({ which }: { which: keyof typeof EQUATIONS }) {
  return (
    <div className="flex items-stretch flex-wrap justify-center gap-y-2">
      {EQUATIONS[which].map((item, i) =>
        item.op ? (
          <div
            key={`op-${i}`}
            className="flex items-center px-2.5 text-[17px] font-medium text-gray-300"
          >
            {item.op}
          </div>
        ) : (
          <div
            key={item.label}
            className="rounded-md border border-black/10 bg-white px-3 py-2 text-[11.5px] font-medium"
            style={{ color: item.color }}
          >
            {item.label}
          </div>
        )
      )}
    </div>
  );
}

interface MiniKpiRow {
  label: string;
  delta: string;
  up: boolean;
  better?: boolean;
  // Only rendered when `detailed` — mirrors MetricDeltaRow's baseline + abs diff.
  baseline?: string;
  change?: string;
}

function MiniKpiCard({
  label,
  value,
  unit,
  labelColor,
  rows,
  tooltip,
  detailed,
}: {
  label: string;
  value: string;
  unit: string;
  labelColor: string;
  rows: MiniKpiRow[];
  tooltip?: string;
  detailed?: boolean;
}) {
  const card = (
    <div
      className={`rounded-lg p-3.5 bg-white border border-black/10 w-full ${
        detailed ? "max-w-[280px]" : "max-w-[210px]"
      }`}
    >
      <div className="text-[11.5px] mb-1.5 font-medium" style={{ color: labelColor }}>
        {label}
      </div>
      <div className="text-[26px] font-semibold tracking-tight leading-none text-gray-900 tabular-nums">
        {value}
        <span className="text-[13px] font-normal ml-1 text-gray-400">{unit}</span>
      </div>
      <div className="h-px bg-gray-200 my-2.5" />
      <div className="flex flex-col gap-1.5">
        {rows.map((r) => (
          <div
            key={r.label}
            className="flex items-center justify-between text-[11px] text-gray-500 gap-2"
          >
            <span className="flex items-center gap-2 min-w-0">
              <span className={detailed ? "w-[62px] shrink-0" : undefined}>{r.label}</span>
              {detailed && r.baseline && <span className="tabular-nums">{r.baseline}</span>}
            </span>
            <span className="flex items-center gap-1.5 shrink-0">
              {detailed && r.change && <span className="tabular-nums opacity-70">{r.change}</span>}
              <MiniDeltaPill up={r.up} value={r.delta} better={r.better} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  if (!tooltip) return card;

  // Renders the hover tooltip inline above the card rather than absolutely
  // positioned, so it can't clip out of the surrounding plinth.
  return (
    <div className="flex flex-col items-center w-full">
      <div
        className={`relative rounded-md bg-white border border-black/10 shadow-md px-2.5 py-1.5 text-[10.5px] text-gray-600 leading-snug mb-3 ${
          detailed ? "max-w-[285px]" : "max-w-[215px]"
        }`}
      >
        {tooltip}
        <span className="absolute left-1/2 -translate-x-1/2 top-full -mt-1 w-2 h-2 rotate-45 bg-white border-r border-b border-black/10" />
      </div>
      {card}
    </div>
  );
}

function DropdownPill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center justify-between gap-1 text-[11px] px-2.5 py-1 rounded-md border border-black/15 bg-white text-gray-600">
      {children}
      <ChevronDown className="h-3 w-3 text-gray-400 shrink-0" />
    </span>
  );
}

const SCOPE_LEVELS = ["All divisions", "All sites", "Dr. Ramirez"];

function MiniScopeDropdowns() {
  return (
    <div className="flex flex-col gap-1.5 w-full">
      {SCOPE_LEVELS.map((s) => (
        <DropdownPill key={s}>{s}</DropdownPill>
      ))}
    </div>
  );
}

// The real Filters toggle, shown in its open state — it gates the drawer holding
// both the scope and period controls, so it's the first thing to point at.
function MiniFiltersButton() {
  return (
    <span
      className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-[13px] font-medium bg-white"
      style={{ borderColor: TEAL, color: TEAL }}
    >
      <SlidersHorizontal className="h-4 w-4" />
      Filters
      <ChevronDown className="h-4 w-4 rotate-180" />
    </span>
  );
}

function MiniSelectedRangeCard() {
  return (
    <div className="inline-flex flex-col items-center rounded-lg border border-black/10 bg-white px-3.5 py-1.5">
      <div className="text-[9px] font-semibold uppercase tracking-[0.6px] text-gray-400">
        Selected Range
      </div>
      <div
        className="flex items-baseline gap-1 text-[12.5px] font-semibold"
        style={{ color: TEAL }}
      >
        <span className="text-[0.82em] font-medium text-gray-500">Mon</span>
        <span className="tabular-nums">Jul 28</span>
        <span className="text-gray-400">–</span>
        <span className="text-[0.82em] font-medium text-gray-500">Sun</span>
        <span className="tabular-nums">Aug 24</span>
      </div>
      <div className="mt-0.5 flex items-baseline gap-1 text-[10px] font-medium text-gray-500">
        <span className="mr-0.5 text-[9px] font-semibold text-gray-400">PY</span>
        <span className="text-[0.82em] font-medium text-gray-400">Mon</span>
        <span className="tabular-nums">Jul 29</span>
        <span className="text-gray-300">–</span>
        <span className="text-[0.82em] font-medium text-gray-400">Sun</span>
        <span className="tabular-nums">Aug 25</span>
      </div>
    </div>
  );
}

// Numbered callout badge. Rendered inline rather than absolutely positioned so it
// survives reflow — these mockups are verified down to 1000px.
function Marker({ n }: { n: number }) {
  return (
    <span
      className="inline-grid place-items-center rounded-full text-white text-[9px] font-bold shrink-0 tabular-nums leading-none"
      style={{ width: 14, height: 14, background: TEAL }}
    >
      {n}
    </span>
  );
}

// Scoped to Central / River Valley, so every ancestor average is pinned — which
// is what shows that a group and division row appear once those are selected.
const PINNED_AVERAGES = [
  { label: "USA Avg.", value: "68%" },
  { label: "Central Avg.", value: "66%" },
  { label: "River Valley Avg.", value: "64%" },
];

function MiniDrillTable({ annotated }: { annotated?: boolean }) {
  const rows = [
    { label: "108 - East Valley", rate: 0.82, marker: undefined },
    { label: "204 - Oak Street", rate: 0.55, marker: 7 },
    { label: "331 - Main St", rate: 0.18, marker: undefined },
  ];
  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium text-gray-800">Drill-down</span>
          {annotated && <Marker n={1} />}
          <span className="text-[11px] px-2.5 py-1 rounded border border-black/15 bg-white text-gray-500">
            Export CSV
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            {annotated && <Marker n={2} />}
            {["%", "Hours"].map((u) => (
              <span key={u} className={togglePill(u === "%")}>
                {u}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {annotated && <Marker n={3} />}
            {["Country", "Group", "Division", "Site", "DVM"].map((l) => (
              <span key={l} className={togglePill(l === "Site")}>
                {l}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="flex items-center justify-between px-2 pb-1.5 text-[11px] font-medium text-gray-500 border-b border-black/10">
          <span>
            Site <span className="text-gray-300">↕</span>
          </span>
          <span className="flex items-center gap-1.5">
            {annotated && <Marker n={4} />}
            Seen % <span className="text-gray-300">↕</span>
          </span>
        </div>
        {PINNED_AVERAGES.map((p, i) => (
          <div
            key={p.label}
            className="flex items-center justify-between px-2 py-1.5 text-[12px] italic font-medium text-gray-500 border-b border-black/5"
            style={{ background: "var(--brand-cream)" }}
          >
            <span className="flex items-center gap-1.5 min-w-0">
              {/* A blank is reserved on the unmarked rows so all three labels line up. */}
              {annotated && (i === 0 ? <Marker n={5} /> : <span className="w-[14px] shrink-0" />)}
              <span className="truncate">{p.label}</span>
            </span>
            <span className="tabular-nums">{p.value}</span>
          </div>
        ))}
        {rows.map((r) => (
          <div
            key={r.label}
            className="flex items-center justify-between px-2 py-1.5 text-[12px] text-gray-600 border-b border-black/5 last:border-0"
          >
            <span className="flex items-center gap-1.5 min-w-0">
              {annotated &&
                (r.marker ? <Marker n={r.marker} /> : <span className="w-[14px] shrink-0" />)}
              <span className="truncate">{r.label}</span>
            </span>
            <span className="flex items-center gap-2 tabular-nums">
              {annotated && r.rate >= 0.75 && <Marker n={6} />}
              <span
                style={
                  r.rate >= 0.75
                    ? { color: "var(--perf-green)", fontWeight: 600 }
                    : r.rate <= 0.25
                      ? { color: "var(--perf-red)", fontWeight: 600 }
                      : undefined
                }
              >
                {(r.rate * 100).toFixed(0)}%
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniFilterChip({ label }: { label: string }) {
  return (
    <Badge
      variant="secondary"
      className="h-6 gap-1 pr-1 text-[11px] font-medium bg-[color-mix(in_srgb,var(--brand-cream)_50%,white)] border border-black/10"
    >
      {label}
      <span className="rounded p-0.5">
        <X className="h-3 w-3" />
      </span>
    </Badge>
  );
}

const TRAILING_PERIODS = ["T1W", "T4W", "T13W", "T52W"];
const CALENDAR_PERIODS = ["PM", "MTD", "YTD"];

function MiniPeriodBar({ compact }: { compact?: boolean }) {
  if (compact) {
    return (
      <div className="flex items-center gap-1.5 flex-wrap justify-center">
        {["T1W", "T4W", "T13W"].map((p) => (
          <span key={p} className={togglePill(p === "T1W")}>
            {p}
          </span>
        ))}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] font-semibold uppercase tracking-[0.7px] text-gray-500">
        Scheduled Period
      </span>
      {TRAILING_PERIODS.map((p) => (
        <span key={p} className={togglePill(p === "T1W")}>
          {p}
        </span>
      ))}
      <span className="w-px h-5 bg-black/15 mx-0.5" />
      {CALENDAR_PERIODS.map((p) => (
        <span key={p} className={togglePill(false)}>
          {p}
        </span>
      ))}
      <span className="text-[11px] text-gray-400 ml-1">As of</span>
      <span className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md border border-black/15 bg-white text-gray-600">
        <CalendarIcon className="h-3.5 w-3.5" />
        Aug 10, 2026
      </span>
    </div>
  );
}

// Every trend chart in the app shares this control layout: what's being measured
// on the bottom left, how it's grouped on the bottom right.
function MiniChartControls({ metrics, activeMetric }: { metrics: string[]; activeMetric: string }) {
  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-1 flex-wrap">
        {metrics.map((m) => (
          <span key={m} className={togglePill(m === activeMetric)}>
            {m}
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[10.5px] text-gray-500 font-medium">Group by</span>
        <div className="flex items-center gap-1">
          {["Day", "Week", "Month"].map((g) => (
            <span key={g} className={togglePill(g === "Week")}>
              {g}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// Two boundary weeks that fall opposite ways, so the majority rule is visible
// rather than asserted. Dates are real: Jul 27 2026 and Aug 31 2026 are Mondays.
const WEEK_SPLITS = [
  {
    range: "Mon Jul 27 → Sun Aug 2",
    parts: [
      { month: "July", days: 5, wins: true },
      { month: "Aug", days: 2, wins: false },
    ],
    verdict: "July",
  },
  {
    range: "Mon Aug 31 → Sun Sep 6",
    parts: [
      { month: "Aug", days: 1, wins: false },
      { month: "Sept", days: 6, wins: true },
    ],
    verdict: "September",
  },
];

function MiniWeekSplit() {
  return (
    <div className="grid grid-cols-2 gap-5">
      {WEEK_SPLITS.map((s) => (
        <div key={s.range}>
          <div className="text-[11px] text-gray-400 mb-1.5 tabular-nums">{s.range}</div>
          <div className="flex rounded-md overflow-hidden h-6">
            {s.parts.map((p) => (
              <div
                key={p.month}
                className="flex items-center justify-center text-[10.5px] font-medium whitespace-nowrap"
                style={{
                  width: `${(p.days / 7) * 100}%`,
                  background: p.wins ? TEAL : "rgba(0,0,0,0.07)",
                  color: p.wins ? "white" : "#666",
                }}
              >
                {p.month} · {p.days}d
              </div>
            ))}
          </div>
          <div className="mt-1.5 text-[12px]">
            <span className="text-gray-400">Counts as </span>
            <strong className="font-semibold" style={{ color: TEAL }}>
              {s.verdict}
            </strong>
          </div>
        </div>
      ))}
    </div>
  );
}

// The tab's four per-unit cards. Illustrative values, in the right order of
// magnitude for each metric -- nothing on the page depends on the arithmetic
// between them.
// Left to right in the order the Revenue tab renders them, so the figure can be
// read against the real cards rather than translated into them.
const REVENUE_METRICS = [
  { label: "per DVM", value: "$12.4K", answers: "what a doctor produces overall" },
  {
    label: "per pet-seen hour",
    value: "$412",
    answers: "what an hour with pets is producing",
  },
  {
    label: "per scheduled hour",
    value: "$251",
    answers: "what an hour on the schedule is producing",
  },
  { label: "per completed appt", value: "$187", answers: "what an average visit is producing" },
  // Last, as on the tab. The only non-currency card, and the only one that is
  // not revenue divided by something -- it is the visit count the other four
  // are divided by, which is why it reads well at the end.
  {
    label: "completed appts/DVM/day",
    value: "10.6",
    answers: "how many visits a doctor gets through in a day",
  },
];

function MiniRevenueMetrics() {
  return (
    <div className="grid grid-cols-5 gap-2.5">
      {REVENUE_METRICS.map((m) => (
        <div key={m.label} className="rounded-lg border border-black/10 bg-white px-3 py-2.5">
          {/* Two lines reserved whether the label needs them or not, the same
              trick KpiCard uses and for the same reason: at five columns the
              longer labels wrap on a narrow window, and without this the five
              headline figures sit on two different baselines, which reads as
              broken rather than as varied. */}
          <div className="text-[11px] text-gray-500 leading-snug min-h-[2.7em]">{m.label}</div>
          <div className="text-[20px] font-semibold tracking-tight text-gray-900 tabular-nums mt-1 leading-none">
            {m.value}
          </div>
          <div className="text-[10.5px] text-gray-400 leading-snug mt-1.5">{m.answers}</div>
        </div>
      ))}
    </div>
  );
}
